import { useMemo } from 'react';
import { useQuery } from 'react-query';
import ReorderChatAPI from '../Api/reorder-chat';
import {
  BackendChatMessage,
  ReorderChat,
  ReorderWorksheetActivity,
} from '../Api/types/reorder-chat';

export type ReorderChatParticipantBadgeType =
  | 'vendor'
  | 'fn_internal'
  | 'influencer';

export type ReorderChatParticipant = {
  id: string;
  name: string;
  badgeType: ReorderChatParticipantBadgeType;
};

export const useGetReorderChat = (
  userId: string,
  styleColor: string | undefined,
) =>
  useQuery<ReorderChat | null>(
    ['reorder-chat', userId, styleColor],
    () => ReorderChatAPI.getChat(userId, styleColor!),
    {
      enabled: !!userId && !!styleColor,
    },
  );

export const useGetReorderChatMessages = (
  userId: string,
  styleColor: string | undefined,
) =>
  useQuery<BackendChatMessage[]>(
    ['reorder-chat-messages', userId, styleColor],
    () => ReorderChatAPI.getChatMessages(userId, styleColor!),
    {
      enabled: !!userId && !!styleColor,
    },
  );

export const useGetReorderActivities = (
  userId: string,
  styleColor: string | undefined,
) =>
  useQuery<ReorderWorksheetActivity[]>(
    ['reorder-activities', userId, styleColor],
    () => ReorderChatAPI.getActivities(userId, styleColor!),
    {
      enabled: !!userId && !!styleColor,
    },
  );

export function useReorderChatParticipants(
  userId: string,
  styleColor: string | undefined,
): { participants: ReorderChatParticipant[]; isLoading: boolean } {
  const chatQuery = useGetReorderChat(userId, styleColor);

  const participants = useMemo<ReorderChatParticipant[]>(() => {
    if (!chatQuery.data) return [];

    const fnParticipants: ReorderChatParticipant[] =
      chatQuery.data.fn_contacts.map((c) => ({
        id: c.id,
        name: 'Fashion Nova',
        badgeType: 'fn_internal' as const,
      }));

    const vendorParticipants: ReorderChatParticipant[] =
      chatQuery.data.vendor_contacts.map((c) => ({
        id: c.id,
        name: c.full_name,
        badgeType: 'vendor' as const,
      }));

    return [...fnParticipants, ...vendorParticipants];
  }, [chatQuery.data]);

  const isLoading = !!userId && !!styleColor && chatQuery.isLoading;

  return { participants, isLoading };
}
